import {
  ContextMenu,
  type DropTargetDropEvent,
  type DropTargetOptions,
  IconButton,
  MenuItem,
  toast,
} from '@affine/component';
import { useAppSettingHelper } from '@affine/core/components/hooks/affine/use-app-setting-helper';
import { CategoryDivider } from '@affine/core/modules/app-sidebar/views';
import { DocsService } from '@affine/core/modules/doc';
import {
  type FolderNode,
  getUncategorizedDocumentIds,
  OrganizeService,
} from '@affine/core/modules/organize';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { AddOrganizeIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';

import {
  NavigationPanelFolderNode,
  NavigationPanelUncategorizedFolderNode,
} from '../../nodes/folder';
import { NavigationPanelTreeRoot } from '../../tree';
import { organizeChildrenDropEffect } from './dnd';
import { RootEmpty } from './empty';
import * as styles from './styles.css';

export const NavigationPanelOrganize = () => {
  const organizeService = useService(OrganizeService);
  const docsService = useService(DocsService);
  const { appSettings } = useAppSettingHelper();
  const path = useMemo(() => ['organize'], []);
  const [newFolderId, setNewFolderId] = useState<string | null>(null);
  const t = useI18n();

  const folderTree = organizeService.folderTree;
  const rootFolder = folderTree.rootFolder;

  const folders = useLiveData(rootFolder.sortedChildren$);
  const isLoading = useLiveData(folderTree.isLoading$);
  const nonTrashDocIds = useLiveData(docsService.list.nonTrashDocsIds$);
  const linkedDocumentIds = useLiveData(folderTree.linkedDocumentIds$);
  const uncategorizedDocIds = useMemo(
    () =>
      appSettings.showUncategorizedFolder
        ? getUncategorizedDocumentIds(nonTrashDocIds, linkedDocumentIds)
        : [],
    [appSettings.showUncategorizedFolder, linkedDocumentIds, nonTrashDocIds]
  );

  const handleCreateFolder = useCallback(() => {
    const newFolderId = rootFolder.createFolder(
      'New Folder',
      rootFolder.indexAt('before')
    );
    track.$.navigationPanel.organize.createOrganizeItem({ type: 'folder' });
    setNewFolderId(newFolderId);
    return newFolderId;
  }, [rootFolder]);

  const handleOnChildrenDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>, node?: FolderNode) => {
      if (!node || !node.id) {
        return; // never happens
      }
      if (
        data.treeInstruction?.type === 'reorder-above' ||
        data.treeInstruction?.type === 'reorder-below'
      ) {
        const at =
          data.treeInstruction?.type === 'reorder-below' ? 'after' : 'before';
        if (data.source.data.entity?.type === 'folder') {
          rootFolder.moveHere(
            data.source.data.entity.id,
            rootFolder.indexAt(at, node.id)
          );
          track.$.navigationPanel.organize.moveOrganizeItem({ type: 'folder' });
        } else {
          toast(t['com.affine.rootAppSidebar.organize.root-folder-only']());
        }
      } else {
        return; // not supported
      }
    },
    [rootFolder, t]
  );

  const createFolderAndDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>) => {
      const newFolderId = handleCreateFolder();
      setNewFolderId(null);
      const newFolder$ = folderTree.folderNode$(newFolderId);

      const entity = data.source.data.entity;
      if (!entity) return;
      const { type, id } = entity;
      if (type !== 'doc' && type !== 'tag' && type !== 'collection') return;

      const folder = newFolder$.value;
      if (!folder) return;
      folder.createLink(type, id, folder.indexAt('after'));
    },
    [folderTree, handleCreateFolder]
  );

  const handleChildrenCanDrop = useMemo<
    DropTargetOptions<AffineDNDData>['canDrop']
  >(() => args => args.source.data.entity?.type === 'folder', []);

  return (
    <div className={styles.root} data-testid="navigation-panel-organize">
      <CategoryDivider label={t['com.affine.rootAppSidebar.organize']()}>
        <IconButton
          data-testid="navigation-panel-bar-add-organize-button"
          onClick={handleCreateFolder}
          size="16"
          tooltip={t[
            'com.affine.rootAppSidebar.explorer.organize-section-add-tooltip'
          ]()}
        >
          <AddOrganizeIcon />
        </IconButton>
      </CategoryDivider>
      <div className={styles.content}>
        <NavigationPanelTreeRoot
          placeholder={
            <RootEmpty
              onClickCreate={handleCreateFolder}
              isLoading={isLoading}
              onDrop={createFolderAndDrop}
            />
          }
        >
          {uncategorizedDocIds.length > 0 ? (
            <NavigationPanelUncategorizedFolderNode
              docIds={uncategorizedDocIds}
              parentPath={path}
            />
          ) : null}
          {folders.map(child => (
            <NavigationPanelFolderNode
              key={child.id}
              nodeId={child.id as string}
              defaultRenaming={child.id === newFolderId}
              onDrop={handleOnChildrenDrop}
              dropEffect={organizeChildrenDropEffect}
              canDrop={handleChildrenCanDrop}
              location={{
                at: 'navigation-panel:organize:folder-node',
                nodeId: child.id as string,
              }}
              parentPath={path}
            />
          ))}
        </NavigationPanelTreeRoot>
        <ContextMenu
          asChild
          items={
            <MenuItem
              prefixIcon={<AddOrganizeIcon />}
              onClick={handleCreateFolder}
            >
              {t['com.affine.rootAppSidebar.organize.add-folder']()}
            </MenuItem>
          }
        >
          <div
            className={styles.blankContextMenuTarget}
            data-testid="navigation-panel-organize-empty-space"
          />
        </ContextMenu>
      </div>
    </div>
  );
};
